import { useMemo, useState } from "react";
import { RefreshCw, Save } from "lucide-react";
import { useQuery } from "convex/react";
import { useUser } from "@clerk/react";
import { PageHeader } from "../PageHeader";
import { AuditRow } from "../shared/AuditRow";
import { FilterChips } from "../shared/FilterChips";
import { downloadAuditNdjson } from "../../../api/client";
import { api as convexApi } from "../../../lib/convexApi";
import { aiAuditDescription, normalizeAuditRow } from "../auditNormalize";
import { usePeople } from "../aiUsage/usePeople";
import { ChainIntegrityBanner, INTEGRITY_ALERT_TYPE } from "../shared/ChainIntegrityBanner";

type Range = "1d" | "7d" | "30d" | "90d";

// Kept in step with auditNormalize's inference: a type carrying a deny word is
// a verdict, not a configuration change.
const DENY_IN_TYPE = /(^|[._-])(deny|denied|block|blocked)([._-]|$)/i;

// The NDJSON export always requests ALL kinds.
//
// Every chip's client meaning has drifted from the route's: `verdict` has no
// server counterpart; `policy` differs because the chip excludes verdicts and
// the route's `startsWith("policy")` includes them; `blocked` differs because
// the chip includes `policy-decision` denials and the route matches only
// `blocked*` types; `dlp` differs because the chip now matches on pattern ids
// as well as the type prefix.
//
// Whitelisting the kinds that happened to agree was a losing game — each is one
// client-side refinement away from silently disagreeing again, and an export
// that omits rows the operator can see is evidence they will trust and should
// not. So the export is unfiltered by kind, the range still applies, and the
// button says so.

const RANGE_DAYS: Record<Range, number> = {
  "1d": 1,
  "7d": 7,
  "30d": 30,
  "90d": 90,
};

// Convex caps audit reads at 500 per query — pre-PR-7 the REST route
// streamed up to 1000; we trade list depth for live updates. 500 is
// 5x the visible viewport on the densest dashboard so it's plenty.
const CAP_PER_QUERY = 500;

function firstString(item: Record<string, unknown>, keys: string[]): string | undefined {
  for (const k of keys) {
    const v = item[k];
    if (typeof v === "string" && v) return v;
  }
  return undefined;
}

// Convex returns row.at as a number; convert to ISO at render time so
// the existing AuditRow API (string `ts`) keeps working without
// shape-shifting downstream consumers.
function asIso(n: unknown): string {
  if (typeof n === "number" && Number.isFinite(n)) {
    return new Date(n).toISOString();
  }
  if (typeof n === "string") return n;
  return "";
}

// Filter-chip values are kind prefixes — e.g. "blocked" matches
// "blocked-command", "dlp" matches "dlp-hit", "approval" matches
// "approval.*" and "approval-*". Pre-PR-7 this was Node-side; doing
// it client-side keeps the Convex query cheap (no per-kind branching
// in the query handler) and lets the reactive snapshot include
// everything in the window, with the chip filtering being purely
// view-state.
//
// G68-2 regression fix: the legacy Node route classified rows by
// SEMANTICS, not just the `type` field prefix. "blocked" matched any
// row with decision === "deny" (so type:"command"+decision:"deny" was
// included); "dlp" matched any row carrying a patternId. Without those
// branches the Convex-backed view silently hid blocked commands and
// inline DLP hits from the filter chips. Restore the semantic checks
// alongside the prefix fallback.
//
// The semantic checks read a FLAT `row.decision`, which raw Convex rows never
// carry (the verdict lives at `details.verdict.decision`), so "Block" matched
// nothing at all — the chip returned "No events match." with real denies on
// screen. Rows are now run through normalizeAuditRow before they get here.
//
// "policy" also over-matched: `type.startsWith("policy")` swept
// `policy-decision` (a verdict) into the "Policy change" chip alongside
// `policy-enforcement-changed` (a config change). Those are different
// questions; the chip means configuration changes.
function matchesKind(row: Record<string, unknown>, kind: string): boolean {
  if (kind === "all") return true;
  if (kind === "blocked" && row.decision === "deny") return true;
  // A row can carry DLP pattern ids under a non-`dlp-` type (an inline hit on a
  // command event), so match on the ids rather than only the type prefix.
  if (kind === "dlp") {
    if (typeof row.patternId === "string" && row.patternId.length > 0) return true;
    const ids = (row.detail as Record<string, unknown> | undefined)?.patternIds;
    if (Array.isArray(ids) && ids.length > 0) return true;
  }
  const type = firstString(row, ["type", "kind"]) ?? "";
  if (!type) return false;
  if (kind === "blocked") {
    // Any KNOWN non-deny verdict wins over the type name. An exception can
    // allow — or an "ask" can pause — a command whose event type is still
    // `blocked-command`, and listing either under the Block chip reports
    // something that did not happen.
    if (row.decision !== undefined && row.decision !== "deny") return false;
    return type.startsWith("blocked-") || type === "blocked";
  }
  if (kind === "dlp") return type.startsWith("dlp-") || type === "dlp" || type === "dlp-hit";
  if (kind === "approval") return type.startsWith("approval");
  // "Policy change" means CONFIGURATION changes — enforcement mode, packs,
  // exceptions. A verdict is a different question and gets its own chip;
  // without one, an allowed or observe-mode policy-decision was reachable only
  // under "All".
  if (kind === "policy") {
    // Exclude verdicts by TYPE, not by the presence of a `decision` field.
    // Keying on the field dropped legitimate configuration events that happen
    // to carry one — and silently hiding a config change from the config
    // filter is a worse error than showing one extra row, because the operator
    // has no way to tell the difference between "nothing changed" and
    // "the filter ate it".
    if (type === "policy-decision" || DENY_IN_TYPE.test(type)) return false;
    return type.startsWith("policy") || type.startsWith("security.exception");
  }
  if (kind === "verdict") return type === "policy-decision" || row.decision !== undefined;
  // The AI gateway's control events (convex/observability/controls.ts).
  if (kind === "ai") return type.startsWith("gateway.") || type.startsWith("ai.");
  return type.startsWith(kind);
}

// Deliberately excludes `agent` — AuditRow already renders it as its own
// field, so repeating it here printed the same value twice on one line.
function auditRowDetails(row: Record<string, unknown>): Record<string, string> | undefined {
  const out: Record<string, string> = {};
  const d = (row.detail ?? {}) as Record<string, unknown>;
  if (typeof row.reason === "string" && row.reason) out.reason = row.reason;
  if (typeof row.packId === "string" && row.packId) out.pack = row.packId;
  if (typeof d.source === "string" && d.source) out.source = d.source;
  // Provenance was stamped on every host-path row and rendered nowhere, so the
  // audit trail could not distinguish a write the host attested to from one a
  // signed-in client vouched for — which is the only thing that distinguishes a
  // forged row here. Shown only when it is NOT attested: "host" is the
  // expected case and labelling it would be noise, while "user" is the one an
  // operator needs to see.
  const provenance = d.provenance ?? (d.details as Record<string, unknown> | undefined)?.provenance;
  if (provenance === "user") out.provenance = "client-vouched";
  // The integrity alert's whole content lives in its details — without these it
  // renders as a bare type with no indication of WHAT broke or where.
  if (row.type === INTEGRITY_ALERT_TYPE) {
    if (typeof d.reason === "string" && d.reason) out.reason = d.reason;
    if (typeof d.brokenAt === "string" && d.brokenAt) out["broken at"] = d.brokenAt;
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

// `isAdmin`: the viewer's role, which the Security Room reads once. A member
// reads the audit log too, and opens only their own AI sessions.
export function AuditPage({ isAdmin = false }: { isAdmin?: boolean } = {}) {
  const [range, setRange] = useState<Range>("7d");
  const [kind, setKind] = useState<string>("all");
  // Names for the people in AI gateway events (an owner, who paused).
  const { nameFor } = usePeople();
  const { user } = useUser();
  const viewer = { userId: user?.id ?? null, isAdmin };

  // Convex returns the most recent CAP_PER_QUERY entries; we then
  // apply the time-window filter client-side. Pattern #14: snapshot
  // is the source of truth — no local mirror to clobber.
  const rows = useQuery((convexApi as any).audit.queries.recent, {
    limit: CAP_PER_QUERY,
  }) as Array<Record<string, unknown>> | undefined;

  const loading = rows === undefined;

  // Memoize the client-side window/kind filter so we don't re-walk the
  // 500-row snapshot on every chip render.
  // Normalized but UNFILTERED. The integrity banner reads this rather than the
  // filtered list on purpose: a tamper alarm that a range chip or a kind chip
  // can hide is not an alarm. The banner prints the alert's own timestamp, so
  // surfacing one from outside the selected window cannot be mistaken for new.
  const normalized = useMemo(() => {
    if (!rows) return [];
    return rows.map((raw) => normalizeAuditRow(raw as Parameters<typeof normalizeAuditRow>[0]));
  }, [rows]);

  const events = useMemo(() => {
    const sinceMs = Date.now() - RANGE_DAYS[range] * 24 * 60 * 60 * 1000;
    // Normalize FIRST, then filter on the normalized timestamp.
    //
    // Filtering the raw row ran `Date.parse` on whatever `at` happened to hold,
    // and a stringified epoch ("1710000000000") is unparseable — so the check
    // evaluated to "keep" and those events escaped every time window. The
    // normalizer already resolves epoch strings, numbers and ISO, so doing it
    // first leaves ONE code path responsible for reading a timestamp. It also
    // flattens the nested verdict, which matchesKind needs.
    return normalized
      .filter((row) => {
        const atMs = Date.parse(row.recordedAt);
        return !(Number.isFinite(atMs) && atMs < sinceMs);
      })
      .filter((row) => matchesKind(row, kind));
  }, [normalized, range, kind]);

  async function downloadNdjson() {
    const since = new Date(Date.now() - RANGE_DAYS[range] * 24 * 60 * 60 * 1000).toISOString();
    try {
      // NDJSON export still goes through the Node route — Convex doesn't
      // serve binary streams from a query, and an export of 100s of MB
      // would blow past the 8 MB query result cap. The Node route is the
      // right place for bulk-exports; it stays alive past PR 7.
      // `verdict` is a client-side chip with no server counterpart, so sending
      // it would have the route silently ignore or mis-filter it — an export
      // that does not match the view it was taken from is worse than an
      // obviously-unfiltered one. Fall back to "all" and say so.
      await downloadAuditNdjson({ since, kind: "all" });
    } catch (e) {
      // Surface in console — minimal UX surface (the route still works).
      console.error("audit export failed:", e);
    }
  }

  return (
    <>
      <PageHeader
        title="Audit log"
        subtitle={`append-only · last ${range} · live`}
        actions={
          <>
            {/* Refresh is a no-op with Convex reactive — but we keep the
                button as a visual affordance + accessibility for users
                who expect it; clicking it just re-mounts the snapshot
                (no-op in practice). */}
            <button
              type="button"
              className="sr-btn sr-btn--ghost"
              onClick={() => void 0}
              disabled={loading}
              title="Audit log updates live (Convex reactive)"
            >
              <RefreshCw size={14} /> Live
            </button>
            <button
              type="button"
              className="sr-btn"
              onClick={() => void downloadNdjson()}
              title="Export the current range as NDJSON. Kind filters are applied in the browser, so the file contains all kinds — filter downstream."
            >
              <Save size={14} /> Export NDJSON
              {kind !== "all" && " (all kinds)"}
            </button>
          </>
        }
      />
      <div className="sr-page-body">
        <ChainIntegrityBanner rows={normalized} />
        <FilterChips
          label="Range"
          value={range}
          onChange={setRange}
          options={[
            { value: "1d", label: "1d" },
            { value: "7d", label: "7d" },
            { value: "30d", label: "30d" },
            { value: "90d", label: "90d" },
          ]}
        />
        {/*
          Chip values map to a kind/type prefix via matchesKind() — see
          docstring on that helper. Keep aligned with the type tags
          written by Convex audit.mutations.record.
        */}
        <FilterChips
          label="Kind"
          value={kind}
          onChange={setKind}
          options={[
            { value: "all", label: "All" },
            { value: "approval", label: "Approval" },
            { value: "policy", label: "Policy change" },
            { value: "verdict", label: "Verdict" },
            { value: "blocked", label: "Block" },
            { value: "dlp", label: "DLP" },
            { value: "ai", label: "AI gateway" },
          ]}
        />
        {loading && <div className="sr-empty">Loading audit…</div>}
        {!loading && events.length === 0 && <div className="sr-empty">No events match.</div>}
        {events.map((raw, i) => {
          // Rows are normalized (see the memo above), so the flat fields are
          // guaranteed present: `recordedAt`, `actor`, `type`, `decision`.
          const e = raw as Record<string, unknown>;
          const id = raw.id || `audit-${i}`;
          const ts = asIso(raw.recordedAt);
          const kindStr = raw.type || "event";
          // The AI gateway's events read as words; their subject ("gateway:pause")
          // is only an identifier, and their actor is named like their people.
          const ai = aiAuditDescription(kindStr, raw.detail, nameFor, viewer);
          const agent = ai && raw.actor ? nameFor(raw.actor) : firstString(e, ["agent", "actor"]);
          const summary =
            ai?.summary ?? firstString(e, ["subject", "summary", "command", "cmd", "reason"]) ?? "";
          const baseDetails = auditRowDetails(raw);
          const details = ai?.details ? { ...(baseDetails ?? {}), ...ai.details } : baseDetails;
          return (
            <AuditRow
              key={id}
              event={{
                id,
                ts,
                agent,
                kind: kindStr,
                summary,
                decision: raw.decision,
                enforced: raw.enforced,
                ruleId: raw.ruleId,
                // Surface the few fields that explain the row. Passing
                // `undefined` meant an operator had to leave the page to find
                // out why an event fired. Deliberately a short allow-list, not
                // the whole blob: `details` can carry arbitrary host-supplied
                // content and dumping it into the row risks both noise and
                // echoing something that should not be rendered.
                details,
                critical: raw.type === INTEGRITY_ALERT_TYPE,
                link: ai?.link,
              }}
            />
          );
        })}
      </div>
    </>
  );
}
